import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';


// Helper component to sync HOD data with local state
const HodDataSync = ({ hodData, selectedDepartment, setSelectedDepartment }) => {
  useEffect(() => {
    if (hodData?.department && !selectedDepartment && hodData.department !== 'Department') {
      console.log('🔄 Syncing department from HodLayout:', hodData.department);
      setSelectedDepartment(hodData.department);
    }
  }, [hodData, selectedDepartment, setSelectedDepartment]);

  return null;
};

const HodLabReview = ({ onNavigate, userInfo }) => {

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form states
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedLabType, setSelectedLabType] = useState('');
  const [selectedLab, setSelectedLab] = useState('');

  // Data states
  const [departments, setDepartments] = useState([]);
  const [labTypes, setLabTypes] = useState([]);
  const [labs, setLabs] = useState([]);
  const [equipment, setEquipment] = useState([]);

  const [labCoordinators, setLabCoordinators] = useState([]);

  // Review states
  const [equipmentStatus, setEquipmentStatus] = useState({});
  const [equipmentNotes, setEquipmentNotes] = useState({});
  const [showEquipmentList, setShowEquipmentList] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // WhatsApp notification states
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [notificationProgress, setNotificationProgress] = useState({});

  // History states
  const [showHistory, setShowHistory] = useState(false);
  const [historyDate, setHistoryDate] = useState('');
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Notification state
  const [notifications, setNotifications] = useState([]);

  // WhatsApp API Configuration
  const INTERAKT_API_URL = 'https://api.interakt.ai/v1/public/message/';
  const INTERAKT_API_KEY = 'Basic Zkt4ZG9nMF9zc0JLYmxhRzFaX1dMQ0FZTHBnaG1qdEp6azlhU1o0ZmpKVTo=';

  // Debug: Log API configuration
  console.log('🔧 WhatsApp API Config:', {
    url: INTERAKT_API_URL,
    hasKey: !!INTERAKT_API_KEY,
    keyPrefix: INTERAKT_API_KEY.substring(0, 10) + '...'
  });

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // API call helper function
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_LINK}/api/hod${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      if (response.status === 401) {
        sessionStorage.clear();
        localStorage.clear();
        if (onNavigate) { onNavigate('home'); } else { window.location.href = '/'; }
        throw new Error('Session expired');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  };

  // Send WhatsApp notification using NEW template
  const sendWhatsAppNotification = async (phoneNumber, coordinatorName, labName, equipmentList, reviewDate, reviewerName) => {
    try {
      console.log(`📱 Sending WhatsApp to ${coordinatorName} at ${phoneNumber}`);

      // **CRITICAL FIX**: Clean data function to remove problematic characters
      const cleanData = (text) => {
        if (!text) return '';
        return text.toString()
          .replace(/\t/g, ' ')           // Replace tabs with single space
          .replace(/\r\n/g, ' ')        // Replace Windows newlines
          .replace(/\n/g, ' ')          // Replace Unix newlines
          .replace(/\r/g, ' ')          // Replace Mac newlines
          .replace(/\s{3,}/g, '  ')     // Replace 3+ consecutive spaces with 2 spaces
          .trim();                      // Remove leading/trailing spaces
      };

      // Sanitize phone number: keep digits only
      let cleanPhone = phoneNumber.toString().replace(/\D/g, '');

      // Remove country code if already included (e.g., 91XXXXXXXXXX)
      if (cleanPhone.length === 12 && cleanPhone.substr(0, 2) === '91') {
        cleanPhone = cleanPhone.substr(2);
      }

      // Final validation: must be exactly 10 digits
      if (cleanPhone.length !== 10) {
        throw new Error('Invalid phone number format');
      }

      // **FIXED**: Clean all data before creating equipment details
      const equipmentDetails = equipmentList.map(eq => {
        const cleanName = cleanData(eq.name);
        const cleanId = cleanData(eq.id);
        const cleanStatus = cleanData(eq.status.toUpperCase());
        const cleanNotes = eq.notes ? cleanData(eq.notes) : '';

        return `${cleanName} (${cleanId}) - ${cleanStatus}${cleanNotes ? ` - ${cleanNotes}` : ''}`;
      }).join(', ');

      // **FIXED**: Clean all template variables
      const cleanLabName = cleanData(labName);
      const cleanReviewDate = cleanData(reviewDate);
      const cleanReviewerName = cleanData(reviewerName);

      // Debug log the cleaned data
      console.log('🧹 Cleaned data:', {
        labName: cleanLabName,
        equipmentDetails: equipmentDetails.substring(0, 100) + '...',
        reviewDate: cleanReviewDate,
        reviewerName: cleanReviewerName
      });

      // Payload structure for NEW template
      const payload = {
        "countryCode": "91",
        "phoneNumber": cleanPhone,
        "callbackData": "lab_maintenance_alert",
        "type": "Template",
        "template": {
          "name": "lab_maintainance_alert", // Your template name
          "languageCode": "en",
          "headerValues": [], // No header variables needed
          "bodyValues": [
            cleanLabName,           // {{1}} - Lab Name (cleaned)
            equipmentDetails,       // {{2}} - Equipment details (cleaned)
            cleanReviewDate,        // {{3}} - Review Date (cleaned)
            cleanReviewerName      // {{4}} - Reviewer Name (cleaned)
          ]
        }
      };

      console.log('📤 Sending WhatsApp payload:', payload);

      const response = await fetch(INTERAKT_API_URL, {
        method: 'POST',
        headers: {
          'Authorization': INTERAKT_API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
        mode: 'cors'
      });

      const result = await response.json();

      console.log('📥 WhatsApp API Response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        result: result
      });

      if (response.status === 201) {
        console.log(`✅ WhatsApp sent successfully to ${coordinatorName}`);
        return { success: true, messageId: result.messageId || result.id, response: result };
      } else {
        console.error(`❌ WhatsApp failed for ${coordinatorName}:`, result);
        return { success: false, error: result.message || `HTTP ${response.status}: ${response.statusText}`, response: result };
      }

    } catch (error) {
      console.error(`💥 WhatsApp error for ${coordinatorName}:`, error);
      return { success: false, error: error.message };
    }
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);



      // Fetch departments, lab types, and coordinators
      const [departmentsData, labTypesData, coordinatorsData] = await Promise.all([
        apiCall('/lab-reviews/departments'),
        apiCall('/lab-reviews/lab-types'),
        apiCall('/lab-reviews/coordinators')
      ]);

      if (departmentsData.success) {
        setDepartments(departmentsData.data);
      }

      if (labTypesData.success) {
        setLabTypes(labTypesData.data);
      }

      if (coordinatorsData.success) {
        setLabCoordinators(coordinatorsData.data);
      }

    } catch (error) {
      console.error('Error fetching initial data:', error);
      setError('Failed to load initial data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch labs when department and type are selected
  const fetchLabs = async (department, labType) => {
    try {
      const response = await apiCall(`/lab-reviews/labs?department=${encodeURIComponent(department)}&type=${encodeURIComponent(labType)}`);

      if (response.success) {
        setLabs(response.data);
      } else {
        showNotification('Failed to fetch labs: ' + response.message, 'error');
        setLabs([]);
      }
    } catch (error) {
      console.error('Error fetching labs:', error);
      showNotification('Failed to fetch labs', 'error');
      setLabs([]);
    }
  };

  // Fetch equipment when lab is selected
  const fetchEquipment = async (labId) => {
    try {
      setLoading(true);
      const response = await apiCall(`/lab-reviews/equipment?labId=${encodeURIComponent(labId)}`);

      if (response.success) {
        setEquipment(response.data);

        // Initialize equipment status and notes
        const initialStatus = {};
        const initialNotes = {};
        response.data.forEach(eq => {
          initialStatus[eq.id] = {
            status: eq.status,
            notificationStatus: 'pending'
          };
          initialNotes[eq.id] = '';
        });
        setEquipmentStatus(initialStatus);
        setEquipmentNotes(initialNotes);
        setShowEquipmentList(true);
      } else {
        showNotification('Failed to fetch equipment: ' + response.message, 'error');
        setEquipment([]);
        setShowEquipmentList(false);
      }
    } catch (error) {
      console.error('Error fetching equipment:', error);
      showNotification('Failed to fetch equipment', 'error');
      setEquipment([]);
      setShowEquipmentList(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch lab review history
  const fetchLabHistory = async (date = null) => {
    try {
      setLoadingHistory(true);

      let endpoint = '/lab-reviews/history';
      if (date) {
        endpoint += `?date=${encodeURIComponent(date)}`;
      }

      const response = await apiCall(endpoint);

      if (response.success) {
        setHistoryData(response.data);
        showNotification('History loaded successfully', 'success');
      } else {
        showNotification('Failed to load history: ' + response.message, 'error');
        setHistoryData([]);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      showNotification('Failed to load history', 'error');
      setHistoryData([]);
    } finally {
      setLoadingHistory(false);
    }
  };



  // Handle department change
  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
    setSelectedLabType('');
    setSelectedLab('');
    setLabs([]);
    setEquipment([]);
    setShowEquipmentList(false);
  };

  // Handle lab type change
  const handleLabTypeChange = (value) => {
    setSelectedLabType(value);
    setSelectedLab('');
    setEquipment([]);
    setShowEquipmentList(false);

    if (value && selectedDepartment) {
      fetchLabs(selectedDepartment, value);
    }
  };

  // Handle lab change and load equipment
  const handleLabChange = (value) => {
    setSelectedLab(value);
    if (value) {
      fetchEquipment(value);
    } else {
      setShowEquipmentList(false);
    }
  };

  // Toggle equipment status
  const toggleEquipmentStatus = (equipmentId, newStatus) => {
    setEquipmentStatus(prev => ({
      ...prev,
      [equipmentId]: {
        ...prev[equipmentId],
        status: newStatus,
        notificationStatus: 'pending'
      }
    }));
  };

  // Update equipment notes
  const updateEquipmentNotes = (equipmentId, notes) => {
    setEquipmentNotes(prev => ({
      ...prev,
      [equipmentId]: notes
    }));
  };

  // Get review statistics
  const getReviewStats = () => {
    const total = equipment.length;
    const working = equipment.filter(eq =>
      equipmentStatus[eq.id]?.status === 'working'
    ).length;
    const maintenance = equipment.filter(eq =>
      equipmentStatus[eq.id]?.status === 'maintenance'
    ).length;
    const notWorking = equipment.filter(eq =>
      equipmentStatus[eq.id]?.status === 'not-working'
    ).length;

    return { total, working, maintenance, notWorking };
  };

  // Show notification
  const showNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type
    };

    setNotifications(prev => [...prev, notification]);

    // Auto remove after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
    }, 5000);
  };

  // Remove notification
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Test WhatsApp function for debugging
  const testWhatsAppConnection = async (hodData) => {
    try {
      console.log('🧪 Testing WhatsApp API connection...');

      const testData = {
        phone: '6290808997',
        coordinatorName: 'Test Coordinator',
        labName: 'Computer Lab 1 (CS-201)',
        equipment: [
          { id: 'TEST-001', name: 'Test Equipment 1', status: 'not-working', notes: 'Screen not working' },
          { id: 'TEST-002', name: 'Test Equipment 2', status: 'maintenance', notes: 'Needs cleaning' }
        ],
        date: new Date().toLocaleDateString('en-IN'),
        reviewerName: hodData.name || 'Test Reviewer'
      };

      console.log('🧪 Sending test data:', testData);

      const result = await sendWhatsAppNotification(
        testData.phone,
        testData.coordinatorName,
        testData.labName,
        testData.equipment,
        testData.date,
        testData.reviewerName
      );

      console.log('🧪 Test result:', result);

      if (result.success) {
        showNotification('✅ Test WhatsApp sent successfully!', 'success');
      } else {
        showNotification(`❌ Test failed: ${result.error}`, 'error');
      }

    } catch (error) {
      console.error('🧪 Test error:', error);
      showNotification(`❌ Test error: ${error.message}`, 'error');
    }
  };

  // Submit lab review with WhatsApp notifications
  const submitLabReview = async (hodData) => {
    if (!selectedDepartment || !selectedLabType || !selectedLab) {
      showNotification('Please select department, lab type, and lab', 'error');
      return;
    }

    if (equipment.length === 0) {
      showNotification('No equipment found to review', 'error');
      return;
    }

    setIsSubmitting(true);
    setSendingNotifications(true);

    try {
      // Prepare review data
      const reviewData = {
        labId: selectedLab,
        reviewDate: new Date().toISOString().split('T')[0],
        reviewerId: hodData.hodId || 'HOD-001',
        reviewerName: hodData.name || 'Department Head',
        equipmentReviews: equipment.map(eq => ({
          equipmentId: eq.id,
          status: equipmentStatus[eq.id]?.status || eq.status,
          notes: equipmentNotes[eq.id] || ''
        }))
      };

      // Submit review to database
      const response = await apiCall('/lab-reviews/submit', {
        method: 'POST',
        body: JSON.stringify(reviewData)
      });

      if (response.success) {
        showNotification('Lab review saved successfully!', 'success');

        // Get equipment needing attention
        const equipmentNeedingAttention = equipment.filter(eq => {
          const status = equipmentStatus[eq.id]?.status || eq.status;
          return status === 'maintenance' || status === 'not-working';
        });

        if (equipmentNeedingAttention.length > 0) {
          showNotification(`Sending WhatsApp notifications for ${equipmentNeedingAttention.length} equipment issues...`, 'info');

          // Find lab coordinator for the selected lab type
          const coordinator = labCoordinators.find(coord => coord.lab_type === selectedLabType);

          if (coordinator && coordinator.phone) {
            console.log('📞 Found coordinator:', coordinator);

            const currentDate = new Date().toLocaleDateString('en-IN');
            const currentLab = labs.find(lab => lab.id === selectedLab);
            const reviewerName = hodData.name || 'Department Head';

            try {
              // Update status to sending for equipment needing attention
              equipmentNeedingAttention.forEach(eq => {
                setEquipmentStatus(prev => ({
                  ...prev,
                  [eq.id]: {
                    ...prev[eq.id],
                    notificationStatus: 'sending'
                  }
                }));
              });

              // Prepare equipment data for WhatsApp
              const equipmentData = equipmentNeedingAttention.map(eq => ({
                id: eq.id,
                name: eq.name,
                status: equipmentStatus[eq.id]?.status || eq.status,
                notes: equipmentNotes[eq.id] || ''
              }));

              console.log('📧 Sending WhatsApp with data:', {
                phone: coordinator.phone,
                coordinator: coordinator.name,
                lab: currentLab?.name || 'Lab',
                equipment: equipmentData,
                date: currentDate,
                reviewer: reviewerName
              });

              const result = await sendWhatsAppNotification(
                coordinator.phone,
                coordinator.name,
                currentLab?.name || 'Lab',
                equipmentData,
                currentDate,
                reviewerName
              );

              if (result.success) {
                // Update status to sent
                equipmentNeedingAttention.forEach(eq => {
                  setEquipmentStatus(prev => ({
                    ...prev,
                    [eq.id]: {
                      ...prev[eq.id],
                      notificationStatus: 'sent'
                    }
                  }));
                });
                console.log(`✅ WhatsApp sent to coordinator ${coordinator.name}`);
                showNotification(`✅ WhatsApp notification sent successfully to ${coordinator.name}!`, 'success');
              } else {
                // Update status to failed
                equipmentNeedingAttention.forEach(eq => {
                  setEquipmentStatus(prev => ({
                    ...prev,
                    [eq.id]: {
                      ...prev[eq.id],
                      notificationStatus: 'failed'
                    }
                  }));
                });
                console.error(`❌ WhatsApp failed for coordinator:`, result.error);
                showNotification(`❌ WhatsApp failed: ${result.error}`, 'error');
              }

            } catch (error) {
              equipmentNeedingAttention.forEach(eq => {
                setEquipmentStatus(prev => ({
                  ...prev,
                  [eq.id]: {
                    ...prev[eq.id],
                    notificationStatus: 'failed'
                  }
                }));
              });
              console.error(`💥 WhatsApp error:`, error);
              showNotification(`❌ WhatsApp error: ${error.message}`, 'error');
            }
          } else {
            console.warn('⚠️ No coordinator found or missing phone:', {
              selectedLabType,
              coordinators: labCoordinators,
              foundCoordinator: coordinator
            });
            showNotification('No coordinator found for this lab type or missing phone number', 'warning');

            // Mark as failed due to missing coordinator
            equipmentNeedingAttention.forEach(eq => {
              setEquipmentStatus(prev => ({
                ...prev,
                [eq.id]: {
                  ...prev[eq.id],
                  notificationStatus: 'failed'
                }
              }));
            });
          }
        } else {
          showNotification('All equipment is working properly - no notifications needed', 'info');
        }
      } else {
        throw new Error(response.message || 'Failed to save review');
      }

    } catch (error) {
      console.error('Error submitting lab review:', error);
      showNotification('Error submitting review: ' + error.message, 'error');
    } finally {
      setIsSubmitting(false);
      setSendingNotifications(false);
    }
  };



  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'working': return '#28a745';
      case 'maintenance': return '#ffc107';
      case 'not-working': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  const stats = getReviewStats();

  return (
    <Layout role="hod" onNavigate={onNavigate} userInfo={userInfo} activePage="hod-lab-review">
      {({ hodData, isMobile }) => (
        <>
          <HodDataSync
            hodData={hodData}
            selectedDepartment={selectedDepartment}
            setSelectedDepartment={setSelectedDepartment}
          />
          {/* Loading Component */}
          {loading && !equipment.length && !departments.length && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 'calc(100vh - 200px)',
              flexDirection: 'column',
              gap: '1rem'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                border: '4px solid #e3f2fd',
                borderTop: '4px solid #238548',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Lab Review...</p>
            </div>
          )}

          {/* Main Content */}
          {(!loading || equipment.length > 0 || departments.length > 0) && (
            <>
              {/* Animations CSS */}
              <style>
                {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideIn {
            from { transform: translateX(400px); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }

          @keyframes pulse {
            0% { opacity: 0.6; }
            50% { opacity: 1; }
            100% { opacity: 0.6; }
          }
        `}
              </style>
              {/* Page Header */}
              <div style={{
                padding: '25px 0 15px 0',
                marginBottom: '25px',
                borderBottom: '1px solid #eee'
              }}>
                <h1 style={{
                  margin: '0 0 10px 0',
                  fontSize: '1.8rem',
                  color: '#333',
                  fontWeight: 600
                }}>
                  Lab Condition Review
                </h1>
                <p style={{
                  color: '#666',
                  fontSize: '0.9rem',
                  margin: 0
                }}>
                  {getCurrentDate()}
                </p>
              </div>

              {/* Error Display */}
              {error && (
                <div style={{
                  background: '#ffebee',
                  border: '1px solid #f5c6cb',
                  color: '#721c24',
                  padding: '1rem',
                  borderRadius: '8px',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.2rem' }}>❌</span>
                  {error}
                </div>
              )}

              {/* Lab Review Section */}
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                marginBottom: '2rem',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: '#238548',
                  color: '#ffffff',
                  padding: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <span style={{ fontSize: '1.8rem' }}>🔬</span>
                  <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Lab Condition Review</h3>
                  {sendingNotifications && (
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '0.9rem',
                      opacity: 0.9,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderRadius: '50%',
                        borderTop: '2px solid white',
                        animation: 'spin 1s infinite linear'
                      }}></div>
                      Sending WhatsApp...
                    </span>
                  )}
                </div>

                <div style={{ padding: '1.5rem' }}>
                  {/* Filter Section */}
                  <div style={{
                    backgroundColor: '#f9f9f9',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                  }}>
                    {/* Department */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '5px',
                        fontWeight: '500',
                        color: '#444'
                      }}>
                        Department:
                      </label>
                      <select
                        value={selectedDepartment}
                        onChange={(e) => handleDepartmentChange(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          transition: 'border-color 0.3s'
                        }}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.name}>{dept.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Lab Type */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '5px',
                        fontWeight: '500',
                        color: '#444'
                      }}>
                        Lab Type:
                      </label>
                      <select
                        value={selectedLabType}
                        onChange={(e) => handleLabTypeChange(e.target.value)}
                        disabled={!selectedDepartment}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: selectedDepartment ? 'white' : '#f5f5f5',
                          transition: 'border-color 0.3s',
                          cursor: selectedDepartment ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <option value="">Select Lab Type</option>
                        {labTypes.map(type => (
                          <option key={type.type} value={type.type}>{type.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Lab Number */}
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '5px',
                        fontWeight: '500',
                        color: '#444'
                      }}>
                        Lab Number:
                      </label>
                      <select
                        value={selectedLab}
                        onChange={(e) => handleLabChange(e.target.value)}
                        disabled={!selectedLabType}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: selectedLabType ? 'white' : '#f5f5f5',
                          transition: 'border-color 0.3s',
                          cursor: selectedLabType ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <option value="">Select Lab</option>
                        {labs.map(lab => (
                          <option key={lab.id} value={lab.id}>{lab.name} ({lab.id})</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Loading indicator for equipment */}
                  {loading && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '2rem',
                      gap: '1rem'
                    }}>
                      <div style={{
                        width: '30px',
                        height: '30px',
                        border: '3px solid #e3f2fd',
                        borderTop: '3px solid #238548',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span>Loading equipment...</span>
                    </div>
                  )}

                  {/* Equipment List */}
                  {showEquipmentList && !loading && (
                    <div>
                      {/* List Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                        paddingBottom: '10px',
                        borderBottom: '1px solid #eee',
                        flexWrap: 'wrap',
                        gap: '10px'
                      }}>
                        <h3 style={{
                          margin: 0,
                          color: '#333',
                          fontSize: '1.2rem'
                        }}>
                          Equipment List
                        </h3>
                        <div style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => {
                              const newStatus = {};
                              equipment.forEach(eq => {
                                newStatus[eq.id] = { status: 'working', notificationStatus: 'pending' };
                              });
                              setEquipmentStatus(newStatus);
                            }}
                            style={{
                              backgroundColor: '#4caf50',
                              color: 'white',
                              border: 'none',
                              padding: '8px 15px',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              transition: 'background-color 0.3s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#388e3c';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#4caf50';
                            }}
                          >
                            ✓ Mark All Working
                          </button>
                          <button
                            onClick={() => {
                              const newStatus = {};
                              equipment.forEach(eq => {
                                newStatus[eq.id] = { status: 'not-working', notificationStatus: 'pending' };
                              });
                              setEquipmentStatus(newStatus);
                            }}
                            style={{
                              backgroundColor: '#f44336',
                              color: 'white',
                              border: 'none',
                              padding: '8px 15px',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              transition: 'background-color 0.3s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#d32f2f';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#f44336';
                            }}
                          >
                            ✕ Mark All Issues
                          </button>
                          <button
                            onClick={() => testWhatsAppConnection(hodData)}
                            style={{
                              backgroundColor: '#2196f3',
                              color: 'white',
                              border: 'none',
                              padding: '8px 15px',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              cursor: 'pointer',
                              transition: 'background-color 0.3s',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#1976d2';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.backgroundColor = '#2196f3';
                            }}
                          >
                            🧪 Test WhatsApp
                          </button>
                        </div>
                      </div>

                      {/* Review Stats */}
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        backgroundColor: '#f5f7fa',
                        borderRadius: '6px',
                        padding: '15px',
                        marginBottom: '20px'
                      }}>
                        <div style={{
                          flex: 1,
                          minWidth: '120px',
                          textAlign: 'center',
                          padding: '0 15px'
                        }}>
                          <span style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            color: '#666',
                            marginBottom: '5px'
                          }}>
                            Total Equipment:
                          </span>
                          <span style={{
                            display: 'block',
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#333'
                          }}>
                            {stats.total}
                          </span>
                        </div>
                        <div style={{
                          flex: 1,
                          minWidth: '120px',
                          textAlign: 'center',
                          padding: '0 15px'
                        }}>
                          <span style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            color: '#666',
                            marginBottom: '5px'
                          }}>
                            Working:
                          </span>
                          <span style={{
                            display: 'block',
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#4caf50'
                          }}>
                            {stats.working}
                          </span>
                        </div>
                        <div style={{
                          flex: 1,
                          minWidth: '120px',
                          textAlign: 'center',
                          padding: '0 15px'
                        }}>
                          <span style={{
                            display: 'block',
                            fontSize: '0.9rem',
                            color: '#666',
                            marginBottom: '5px'
                          }}>
                            Issues:
                          </span>
                          <span style={{
                            display: 'block',
                            fontSize: '1.5rem',
                            fontWeight: '700',
                            color: '#f44336'
                          }}>
                            {stats.maintenance + stats.notWorking}
                          </span>
                        </div>
                      </div>

                      {/* Excel-Style Equipment List */}
                      {equipment.length > 0 ? (
                        <div className="excel-container" style={{
                          overflowX: 'auto',
                          border: '1px solid #d4d4d4',
                          borderRadius: '4px',
                          marginBottom: '20px',
                          backgroundColor: 'white'
                        }}>
                          <style>
                            {`
                          .excel-table {
                            width: 100%;
                            border-collapse: collapse;
                            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                            font-size: 13px;
                          }
                          .excel-table th {
                            background-color: #f3f3f3;
                            color: #333;
                            font-weight: 600;
                            text-align: left;
                            padding: 8px 12px;
                            border: 1px solid #d4d4d4;
                            white-space: nowrap;
                            position: sticky;
                            top: 0;
                            z-index: 10;
                          }
                          .excel-table td {
                            border: 1px solid #d4d4d4;
                            padding: 0; 
                            color: #333;
                            height: 32px; /* Fixed row height like Excel */
                            vertical-align: middle;
                          }
                          .excel-cell-content {
                            padding: 4px 12px;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                          }
                          .excel-input {
                            width: 100%;
                            height: 100%;
                            border: none;
                            padding: 4px 12px;
                            font-family: inherit;
                            font-size: inherit;
                            background-color: transparent;
                            outline: none;
                            box-sizing: border-box;
                          }
                          .excel-input:focus {
                            box-shadow: inset 0 0 0 2px #217346; /* Excel green focus */
                            background-color: #fff;
                          }
                          .excel-select {
                            width: 100%;
                            height: 100%;
                            border: none;
                            padding: 0 8px;
                            font-family: inherit;
                            font-size: inherit;
                            background-color: transparent;
                            outline: none;
                            cursor: pointer;
                            -webkit-appearance: none;
                            -moz-appearance: none;
                            appearance: none;
                          }
                          .excel-select:focus {
                             box-shadow: inset 0 0 0 2px #217346;
                             background-color: #fff;
                          }
                          
                          /* Custom status colors for the cell background when selected */
                          .status-working { background-color: #e6fffa; color: #00695c; }
                          .status-maintenance { background-color: #fff8e1; color: #ff8f00; }
                          .status-not-working { background-color: #ffebee; color: #c62828; }
                          
                          .excel-row:hover td {
                            background-color: #f5f5f5;
                          }
                        `}
                          </style>
                          <table className="excel-table">
                            <thead>
                              <tr>
                                <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                                <th style={{ width: '100px' }}>Room No.</th>
                                <th style={{ width: '120px' }}>ID</th>
                                <th style={{ minWidth: '200px' }}>Equipment Name</th>
                                <th style={{ width: '150px' }}>Category</th>
                                <th style={{ width: '160px' }}>Status</th>
                                <th style={{ minWidth: '250px' }}>Notes / Remarks</th>
                                <th style={{ width: '140px', textAlign: 'center' }}>Notification</th>
                              </tr>
                            </thead>
                            <tbody>
                              {equipment.map((eq, index) => {
                                const eqStatus = equipmentStatus[eq.id] || { status: eq.status, notificationStatus: 'pending' };
                                const currentNote = equipmentNotes[eq.id] || '';
                                const currentLabInfo = labs.find(l => l.id === selectedLab);
                                const roomNumber = currentLabInfo ? currentLabInfo.id : selectedLab;

                                // Determine cell class based on status for visual feedback
                                let statusCellClass = '';
                                if (eqStatus.status === 'working') statusCellClass = 'status-working';
                                else if (eqStatus.status === 'maintenance') statusCellClass = 'status-maintenance';
                                else if (eqStatus.status === 'not-working') statusCellClass = 'status-not-working';

                                return (
                                  <tr key={eq.id} className="excel-row">
                                    <td style={{ textAlign: 'center', backgroundColor: '#f9f9f9', fontWeight: '500', color: '#666' }}>
                                      {index + 1}
                                    </td>
                                    <td>
                                      <div className="excel-cell-content" title="Room Number">{roomNumber}</div>
                                    </td>
                                    <td>
                                      <div className="excel-cell-content" title={eq.id}>{eq.id}</div>
                                    </td>
                                    <td>
                                      <div className="excel-cell-content" style={{ fontWeight: 500 }} title={eq.name}>{eq.name}</div>
                                    </td>
                                    <td>
                                      <div className="excel-cell-content" title={eq.category}>{eq.category}</div>
                                    </td>
                                    <td className={statusCellClass} style={{ position: 'relative' }}>
                                      <select
                                        className="excel-select"
                                        value={eqStatus.status}
                                        onChange={(e) => toggleEquipmentStatus(eq.id, e.target.value)}
                                        style={{ fontWeight: 500 }}
                                      >
                                        <option value="working">Working</option>
                                        <option value="maintenance">Needs Maintenance</option>
                                        <option value="not-working">Not Working</option>
                                      </select>
                                      <span style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none',
                                        fontSize: '10px',
                                        color: '#666'
                                      }}>▼</span>
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        className="excel-input"
                                        placeholder="Enter remarks..."
                                        value={currentNote}
                                        onChange={(e) => updateEquipmentNotes(eq.id, e.target.value)}
                                      />
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div style={{
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        display: 'inline-block',
                                        backgroundColor:
                                          eqStatus.notificationStatus === 'pending' ? '#eeeeee' :
                                            eqStatus.notificationStatus === 'sending' ? '#e3f2fd' :
                                              eqStatus.notificationStatus === 'sent' ? '#e8f5e9' : '#ffebee',
                                        color:
                                          eqStatus.notificationStatus === 'pending' ? '#757575' :
                                            eqStatus.notificationStatus === 'sending' ? '#1565c0' :
                                              eqStatus.notificationStatus === 'sent' ? '#2e7d32' : '#c62828',
                                      }}>
                                        {eqStatus.notificationStatus === 'pending' ? 'Pending' :
                                          eqStatus.notificationStatus === 'sending' ? 'Sending...' :
                                            eqStatus.notificationStatus === 'sent' ? 'Sent' : 'Failed'}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div style={{
                          textAlign: 'center',
                          padding: '3rem',
                          color: '#666',
                          border: '1px dashed #ccc',
                          borderRadius: '8px',
                          backgroundColor: '#f9f9f9'
                        }}>
                          <span style={{ fontSize: '2.5rem', display: 'block', marginBottom: '1rem' }}>🔧</span>
                          <p style={{ margin: 0, fontSize: '1.1rem' }}>No equipment records found.</p>
                          <p style={{ fontSize: '0.9rem', color: '#888', marginTop: '0.5rem' }}>Select a valid Department and Lab Type to view data.</p>
                        </div>
                      )}

                      {/* Submit Button */}
                      {equipment.length > 0 && (
                        <div style={{
                          marginTop: '20px',
                          textAlign: 'center'
                        }}>
                          <button
                            onClick={() => submitLabReview(hodData)}
                            disabled={isSubmitting || sendingNotifications}
                            style={{
                              backgroundColor: (isSubmitting || sendingNotifications) ? '#78a9e6' : '#238548',
                              color: 'white',
                              border: 'none',
                              padding: '12px 25px',
                              borderRadius: '4px',
                              fontSize: '1rem',
                              fontWeight: '500',
                              cursor: (isSubmitting || sendingNotifications) ? 'not-allowed' : 'pointer',
                              transition: 'background-color 0.3s, transform 0.2s',
                              minWidth: '250px',
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSubmitting && !sendingNotifications) {
                                e.target.style.backgroundColor = '#1e7139';
                                e.target.style.transform = 'translateY(-2px)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isSubmitting && !sendingNotifications) {
                                e.target.style.backgroundColor = '#238548';
                                e.target.style.transform = 'translateY(0)';
                              }
                            }}
                          >
                            {isSubmitting || sendingNotifications ? (
                              <>
                                <span style={{
                                  width: '16px',
                                  height: '16px',
                                  border: '2px solid rgba(255,255,255,0.3)',
                                  borderRadius: '50%',
                                  borderTop: '2px solid white',
                                  animation: 'spin 1s infinite linear',
                                  display: 'inline-block',
                                  marginRight: '8px'
                                }} />
                                {sendingNotifications ? 'Sending WhatsApp...' : 'Processing...'}
                              </>
                            ) : (
                              <>📱 Submit Review & Send WhatsApp</>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No selection state */}
                  {!selectedLab && !loading && (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem',
                      color: '#666'
                    }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔬</div>
                      <h3 style={{ margin: '0 0 1rem 0' }}>Select Lab for Review</h3>
                      <p style={{ margin: 0 }}>
                        Please select a department, lab type, and specific lab to begin the equipment review process.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Review History Section */}
              <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden'
              }}>
                <div style={{
                  background: '#238548',
                  color: '#ffffff',
                  padding: '1.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <span style={{ fontSize: '1.8rem' }}>📊</span>
                  <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Review History</h3>
                </div>

                <div style={{ padding: '1.5rem' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px',
                    flexWrap: 'wrap',
                    gap: '10px'
                  }}>
                    <p style={{ margin: 0, color: '#666' }}>
                      View past lab review records by date.
                    </p>
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      style={{
                        backgroundColor: '#f0f2f5',
                        color: '#333',
                        border: 'none',
                        padding: '8px 15px',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        transition: 'background-color 0.3s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#e0e2e5';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#f0f2f5';
                      }}
                    >
                      🔍 {showHistory ? 'Hide' : 'View'} History
                    </button>
                  </div>

                  {showHistory && (
                    <div>
                      <div style={{
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                        padding: '15px',
                        marginBottom: '20px',
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '15px'
                      }}>
                        <div>
                          <label style={{
                            display: 'block',
                            marginBottom: '5px',
                            fontWeight: '500',
                            color: '#444'
                          }}>
                            Select Date:
                          </label>
                          <input
                            type="date"
                            value={historyDate}
                            onChange={(e) => setHistoryDate(e.target.value)}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: 'white'
                            }}
                          />
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'end'
                        }}>
                          <button
                            onClick={() => fetchLabHistory(historyDate)}
                            disabled={loadingHistory}
                            style={{
                              backgroundColor: loadingHistory ? '#ccc' : '#238548',
                              color: 'white',
                              border: 'none',
                              padding: '10px 15px',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              cursor: loadingHistory ? 'not-allowed' : 'pointer',
                              transition: 'background-color 0.3s',
                              height: 'fit-content'
                            }}
                            onMouseEnter={(e) => {
                              if (!loadingHistory) {
                                e.target.style.backgroundColor = '#1e7139';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loadingHistory) {
                                e.target.style.backgroundColor = '#238548';
                              }
                            }}
                          >
                            {loadingHistory ? '⏳ Loading...' : '🔍 Load Records'}
                          </button>
                        </div>
                      </div>

                      {/* History Results */}
                      {historyData.length > 0 ? (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse'
                          }}>
                            <thead>
                              <tr>
                                <th style={{
                                  padding: '12px 15px',
                                  textAlign: 'left',
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: '#f8f9fa',
                                  fontWeight: '600',
                                  color: '#333'
                                }}>
                                  Lab Name
                                </th>
                                <th style={{
                                  padding: '12px 15px',
                                  textAlign: 'left',
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: '#f8f9fa',
                                  fontWeight: '600',
                                  color: '#333'
                                }}>
                                  Review Date
                                </th>
                                <th style={{
                                  padding: '12px 15px',
                                  textAlign: 'center',
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: '#f8f9fa',
                                  fontWeight: '600',
                                  color: '#333'
                                }}>
                                  Equipment Count
                                </th>
                                <th style={{
                                  padding: '12px 15px',
                                  textAlign: 'center',
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: '#f8f9fa',
                                  fontWeight: '600',
                                  color: '#333'
                                }}>
                                  Working
                                </th>
                                <th style={{
                                  padding: '12px 15px',
                                  textAlign: 'center',
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: '#f8f9fa',
                                  fontWeight: '600',
                                  color: '#333'
                                }}>
                                  Issues
                                </th>
                                <th style={{
                                  padding: '12px 15px',
                                  textAlign: 'left',
                                  borderBottom: '1px solid #eee',
                                  backgroundColor: '#f8f9fa',
                                  fontWeight: '600',
                                  color: '#333'
                                }}>
                                  Reviewer
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {historyData.map(record => (
                                <tr key={record.id} style={{
                                  borderBottom: '1px solid #eee',
                                  transition: 'background-color 0.3s'
                                }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f5f9ff';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}>
                                  <td style={{ padding: '12px 15px' }}>
                                    {record.labName} ({record.labType})
                                  </td>
                                  <td style={{ padding: '12px 15px' }}>
                                    {formatDate(record.reviewDate)}
                                  </td>
                                  <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                                    {record.equipmentSummary.total}
                                  </td>
                                  <td style={{ padding: '12px 15px', textAlign: 'center', color: '#28a745' }}>
                                    {record.equipmentSummary.working}
                                  </td>
                                  <td style={{ padding: '12px 15px', textAlign: 'center', color: '#dc3545' }}>
                                    {record.equipmentSummary.broken + record.equipmentSummary.maintenance}
                                  </td>
                                  <td style={{ padding: '12px 15px' }}>
                                    {record.reviewerName}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : showHistory && !loadingHistory && (
                        <div style={{
                          textAlign: 'center',
                          padding: '2rem',
                          color: '#666'
                        }}>
                          <span style={{ fontSize: '2rem', display: 'block', marginBottom: '1rem' }}>📋</span>
                          No review history found for the selected criteria.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>


              {/* Notifications */}
              <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 9999
              }}>
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    style={{
                      maxWidth: '350px',
                      padding: '0',
                      borderRadius: '4px',
                      boxShadow: '0 3px 10px rgba(0,0,0,0.2)',
                      marginBottom: '10px',
                      overflow: 'hidden',
                      animation: 'slideIn 0.3s ease-out forwards',
                      backgroundColor:
                        notification.type === 'success' ? '#e8f5e9' :
                          notification.type === 'warning' ? '#fff3e0' :
                            notification.type === 'error' ? '#ffebee' : '#e3f2fd',
                      borderLeft: `4px solid ${notification.type === 'success' ? '#4caf50' :
                        notification.type === 'warning' ? '#ff9800' :
                          notification.type === 'error' ? '#f44336' : '#2196f3'
                        }`
                    }}
                  >
                    <div style={{
                      padding: '15px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <span style={{
                        color:
                          notification.type === 'success' ? '#1b5e20' :
                            notification.type === 'warning' ? '#e65100' :
                              notification.type === 'error' ? '#b71c1c' : '#0d47a1'
                      }}>
                        {notification.message}
                      </span>
                      <button
                        onClick={() => removeNotification(notification.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#666',
                          fontSize: '1.2rem',
                          cursor: 'pointer',
                          marginLeft: '10px',
                          padding: 0
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>

            </>
          )}
        </>
      )}
    </Layout>
  );
};

export default HodLabReview;